import { assertRequiredColumns } from '../assertRequiredColumns';
import {
  asChartData,
  axisNumberFormat,
  chartPresentationProps,
  seriesToChartConfig,
} from '../presentation';
import { splitWaterfallDelta } from '../splitWaterfallDelta';
import type { TransformRow } from '../../transforms/types';
import type {
  RenderAdapterResult,
  WaterfallAdapterConfig,
  WaterfallAdapterProps,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';

function waterfallProps(
  config: WaterfallAdapterConfig | undefined,
  data: TransformRow[],
): WaterfallAdapterProps {
  const positiveKey = config?.positiveSeriesKey ?? '';
  const negativeKey = config?.negativeSeriesKey ?? '';

  return {
    ...config?.presentation?.passthrough,
    data: asChartData(data),
    series: [positiveKey, negativeKey],
    xAxisKey: config?.segmentKey ?? '',
    // A waterfall's value axis is its X axis: segments run down the Y axis and
    // the signed delta runs across.
    xAxisFormat: axisNumberFormat(config?.presentation?.valueAxisFormat),
    config: seriesToChartConfig(
      [positiveKey, negativeKey],
      config?.presentation?.series,
    ),
    ...chartPresentationProps(config?.presentation),
  };
}

export function waterfallAdapter(
  rows: unknown,
  config: WaterfallAdapterConfig,
): RenderAdapterResult<WaterfallAdapterProps> {
  const validated = validateRowsInput(rows, waterfallProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  const columnError = assertRequiredColumns(validated.rows, [
    config.segmentKey,
    config.valueKey,
  ]);
  if (columnError) {
    return { props: waterfallProps(config, []), error: columnError };
  }

  return {
    props: waterfallProps(
      config,
      splitWaterfallDelta(validated.rows, {
        valueKey: config.valueKey,
        positiveSeriesKey: config.positiveSeriesKey,
        negativeSeriesKey: config.negativeSeriesKey,
      }),
    ),
  };
}
